import { useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import ProfileCompletionPercentage from "./ProfileCompletionPercentage";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { getKybPeopleReasons, getKybReasons } from "../../helper/kybReasons";
import {
  getGuarantorStatus,
  KYC_TOOLTIP_PRIORITY,
  getKycConsiderResult,
  getKycDocumentStatus,
} from "../../helper/common";
import { resolveFinancialDisplayError } from "../../Utils/bankStatementClassification";
import {
  Avatar,
  Badge,
  Button,
  Icon,
  Modal,
  RowButton,
  Skeleton,
  SkorProtectBadge,
} from "../design";
import styles from "./TenantHeaderCard.module.css";
import { getKycScoreState } from "../../helper/profileCompletionScore";

const TenantHeaderCard = ({
  percent,
  percentageLoading = false,
  missingDocs = [],
  bankConnected = false,
  financial_info_errors = null,
  bankStatementClassification = null,
  rentalStatus = null,
  guarantorCount = 0,
  guarantorCompleteCount = null,
  guarantorPendingCount = null,
  gurantorsIncome = [],
  flatmateCount = 0,
  // Number of missing documents the tenant covered with their explanation
  // note. Shown next to the "still to upload" count; the completion %
  // keeps using the full missing list.
  explainedDocsCount = 0,
  pendingActionCount = 0,
  onPendingActionClick,
  onRedoKyc,
  onConnectBank,
  onAddGuarantor,
  onAddFlatmate,
  onSetTargetRent,
  onEditLandlordNote,
  onOpenGuarantor = null,
  disabled = false,
  processing = false,
  readOnly = false,
  user = null,
  llShowOnly = false,
  isGuarantor = false,
  // Company login tenants: KYB and signatories' KYC tiles only.
  isCompanyTenant = false,
  signatoryKycState = "pending",
}) => {
  // #NOTE: We are not considering KYC AML here, only to tenant
  const kycState = getKycScoreState(user, null, false);
  console.log("KycState", kycState);
  const { _t } = useTranslator();
  const [expanded, setExpanded] = useState(false);
  const [kycApprovedOpen, setKycApprovedOpen] = useState(false);
  const visibleDocs = expanded ? missingDocs : missingDocs.slice(0, 3);
  const normalizedRentalStatus =
    rentalStatus === null || rentalStatus === undefined
      ? null
      : String(rentalStatus);
  const isKycFailed =
    kycState === _t("declined") ||
    kycState === "declined" ||
    kycState === _t("failed") ||
    kycState === "failed" ||
    kycState === "review";
  const isKycOk = !isKycFailed;
  const isRentalElevated = normalizedRentalStatus?.toLowerCase() === "elevated";
  const hasRentalStatus =
    normalizedRentalStatus !== null &&
    normalizedRentalStatus !== undefined &&
    normalizedRentalStatus.trim() !== "";
  // Main actions are lime buttons; weak actions are plain text on the dark card.
  const strongAction = {
    variant: "accent",
    size: "sm",
    className: styles.action,
  };
  const weakAction = {
    variant: "ghost",
    size: "sm",
    className: `${styles.action} ${styles.onDark}`,
  };
  const kycAction = isKycOk ? weakAction : strongAction;
  // Approved KYC: no Redo button; the tile opens a popup with a redo link.
  const isKycApproved = kycState === "approved";
  const KycBox = isKycApproved && !readOnly ? RowButton : "div";
  const kycBoxProps =
    KycBox === RowButton
      ? {
          className: `${styles.box} ${styles.boxClickable}`,
          onClick: () => setKycApprovedOpen(true),
        }
      : { className: styles.box };
  const redoIcon = (
    <img src="/images/redoIcon.svg" alt="" className={styles.btnIcon} />
  );
  const disabledTooltip = readOnly
    ? _t("viewOnly", "View only — you can't make changes to this profile.")
    : processing
      ? _t(
          "profileProcessingWait",
          "Your profile is still being processed. Please wait until it's ready."
        )
      : disabled
        ? _t(
            "profileOutdatedRenewFirst",
            "Your profile is outdated. Please renew your profile first."
          )
        : undefined;

  const renderTooltip = (id) =>
    disabled ? (
      <ReactTooltip
        id={id}
        place="top"
        className="react-tooltip-styling"
        render={() => <p>{disabledTooltip}</p>}
      />
    ) : null;

  const getReviewReason = () => {
    const kycUser = user?.onfido_user_kyc;
    const status = getKycDocumentStatus(user);
    const kycChecks = kycUser?.kyc_checks;

    if (
      status === "review" &&
      kycUser?.kyc_data?.document_type === "unidentified"
    ) {
      return llShowOnly
        ? _t(
            "onfidoUnidentifiedDocumentReviewLandlordMessage",
            "The tenant uploaded unsupported identification documents, so identity verification could not be completed successfully. Please ask the tenant to upload a valid government-issued ID with a visible photo. Copies or scanned documents are not accepted."
          )
        : _t(
            "onfidoUnidentifiedDocumentReviewTenantMessage",
            "Please upload a photo of a valid identification document that clearly shows your face. Copies or scanned documents are not accepted."
          );
    }

    if (!kycChecks) return _t("onfidoFallbackMessage");

    const isSanctions = getKycConsiderResult(
      kycChecks,
      "image_integrity.supported_document.properties.issuing_country_subject_to_sanctions"
    );

    if (isSanctions) {
      return _t("onfidoImageIntegritySanctionsMessage");
    }

    for (const { path, message, explanation } of KYC_TOOLTIP_PRIORITY) {
      if (getKycConsiderResult(kycChecks, path)) {
        if (path?.includes("image_quality")) {
          return `${_t(message)}: ${_t(explanation)}`;
        }
        return _t(message);
      }
    }

    return kycChecks.result === "consider" ? _t("onfidoFallbackMessage") : null;
  };

  const kycReviewReason = getReviewReason();
  const pendingActionLabel =
    pendingActionCount === 1 ? _t("pendingAction") : _t("pendingActions");

  const isGuarantorFor = isGuarantor || (user?.gurantor_for?.length ?? 0) > 0;
  const guarantorForCount = user?.gurantor_for?.length ?? 0;
  const canOpenGuarantorFor =
    isGuarantorFor &&
    guarantorForCount > 0 &&
    typeof onOpenGuarantor === "function";

  // Guarantor complete / pending breakdown for the header value.
  // Complete = accepted guarantor whose profile is finished (forensic ready).
  // Everything else (pending invite, processing, docs/KYC missing) = pending.
  const getGuarantorCounts = () => {
    if (guarantorCompleteCount !== null && guarantorPendingCount !== null) {
      return {
        complete: guarantorCompleteCount,
        pending: guarantorPendingCount,
      };
    }
    const entries = Array.isArray(user?.gurantor_added_id)
      ? user.gurantor_added_id
      : [];
    const flagged = Array.isArray(
      user?.tenant_extra_field_id?.selected_onboarding_users
    )
      ? user.tenant_extra_field_id.selected_onboarding_users.filter(
          (item) => item?.type === "guarantor"
        )
      : [];
    let complete = 0;
    let pending = flagged.length;
    entries.forEach((entry) => {
      const match = (gurantorsIncome ?? []).find(
        (g) => g?.all_sources?._id === entry?.guarantor_id?._id
      );
      let statusKey = null;
      if (match) {
        statusKey = getGuarantorStatus("accepted", match?.all_sources)?.status;
      } else {
        statusKey = getGuarantorStatus(
          entry?.status,
          entry?.guarantor_id
        )?.status;
      }
      if (statusKey === "completed") complete += 1;
      else pending += 1;
    });
    // Fallback to the numeric prop when user data is not yet available
    if (entries.length === 0 && flagged.length === 0 && guarantorCount > 0) {
      pending = guarantorCount;
    }
    return { complete, pending };
  };
  const { complete: guarantorComplete, pending: guarantorPending } =
    getGuarantorCounts();
  const totalGuarantors = guarantorComplete + guarantorPending;
  const guarantorValueLabel =
    totalGuarantors === 0
      ? _t("none", "None")
      : guarantorComplete > 0 && guarantorPending === 0
        ? `${guarantorComplete} ${_t("complete", "Complete")}`
        : guarantorComplete === 0 && guarantorPending > 0
          ? `${guarantorPending} ${_t("pending", "Pending")}`
          : `${guarantorComplete} ${_t("complete", "Complete")} · ${guarantorPending} ${_t("pending", "Pending")}`;
  const guarantorCtaLabel =
    totalGuarantors === 0 ? (
      <>+ {_t("add", "Add")}</>
    ) : (
      _t("addorview", "Add/View")
    );
  const applicationValueLabel =
    !flatmateCount || flatmateCount === 0
      ? _t("none", "None")
      : `${flatmateCount}`;
  const applicationCtaLabel =
    !flatmateCount || flatmateCount === 0 ? (
      <>+ {_t("new", "New")}</>
    ) : (
      _t("addorview", "Add/View")
    );

  // Bank behaviour error: classification wins over Algoan error when available;
  // unknown/missing classification falls back to the Algoan error.
  const bankDisplayError = resolveFinancialDisplayError({
    classification: bankStatementClassification,
    financialInfoErrors: financial_info_errors,
    userFinancialInfoErrors: user?.financial_info_errors,
  });
  const hasBankError = Boolean(bankDisplayError.key);
  const bankErrorTitle = bankDisplayError.titleKey
    ? _t(bankDisplayError.titleKey, bankDisplayError.titleFallback)
    : null;
  const bankErrorSubtitle = bankDisplayError.subtitleKey
    ? _t(bankDisplayError.subtitleKey, bankDisplayError.subtitleFallback)
    : null;
  const bankErrorText = hasBankError
    ? bankErrorSubtitle || _t(bankDisplayError.key, bankDisplayError.fallback)
    : null;

  const renderVerificationState = (state) => {
    const icon =
      state === "approved"
        ? "/images/trusted.svg"
        : state === "review"
          ? "/images/warning.svg"
          : state === "declined" || state === "failed"
            ? "/images/high-risk.svg"
            : null;
    return (
      <span className={styles.kycErrorTrigger}>
        <span className={styles.kycErrorIcon} aria-hidden="true">
          {icon && <img src={icon} alt="" width="16" height="16" />}
        </span>
        <span>{_t(state)}</span>
      </span>
    );
  };

  if (isCompanyTenant) {
    // Review / declined reasons, a signatory's reason with their name.
    const kybReasons = getKybReasons(user, _t, llShowOnly);
    const signatoryReasons = getKybPeopleReasons(user, _t, llShowOnly);
    const renderCompanyState = (state, reasons, tooltipId) =>
      reasons.length ? (
        <>
          <span data-tooltip-id={tooltipId}>
            {renderVerificationState(state)}
          </span>
          <ReactTooltip
            id={tooltipId}
            place="bottom"
            className={styles.kycTooltip}
            render={() => (
              <div>
                {reasons.map((reason) => (
                  <p key={reason} className={styles.kycTooltipText}>
                    {reason}
                  </p>
                ))}
              </div>
            )}
          />
        </>
      ) : (
        renderVerificationState(state)
      );
    const companyName =
      user?.onfido_user_kyc?.kyb_data?.company_name ||
      user?.company_legal_name ||
      "";
    return (
      <section className={styles.card} aria-label="company overview">
        <div className={styles.top}>
          <div className={styles.companyHeading}>
            <Avatar name={companyName} tone="lime" size="lg" />
            <h2 className={styles.companyName}>{companyName}</h2>
            <Badge tone="onDark">
              {_t(
                "companyBadge",
                "Company profile header: badge next to the company name"
              )}
            </Badge>
          </div>
        </div>
        <div className={`${styles.grid} ${styles.gridTwo}`}>
          <div className={styles.box}>
            <div className={styles.boxLabel}>
              {_t(
                "companyVerificationKyb",
                "Company profile header: tile label for the Didit KYB business verification status"
              )}
            </div>
            <div className={styles.boxValue}>
              {renderCompanyState(kycState, kybReasons, "thc-kyb-reason")}{" "}
              {!readOnly &&
                (disabled ? (
                  <div
                    style={{ display: "inline-block", marginLeft: "auto" }}
                    data-tooltip-id="thc-kyb-redo-tooltip"
                  >
                    <Button
                      {...kycAction}
                      iconStart={redoIcon}
                      onClick={onRedoKyc}
                      disabled={disabled}
                    >
                      {_t("redo", "Redo")}
                    </Button>
                    {renderTooltip("thc-kyb-redo-tooltip")}
                  </div>
                ) : (
                  <Button
                    {...kycAction}
                    iconStart={redoIcon}
                    onClick={onRedoKyc}
                  >
                    {_t("redo", "Redo")}
                  </Button>
                ))}
            </div>
          </div>
          <div className={styles.box}>
            <div className={styles.boxLabel}>
              {_t(
                "authorisedSignatoryKyc",
                "Company profile header: tile label for the combined KYC status of all authorised signatories"
              )}
            </div>
            <div className={styles.boxValue}>
              {renderCompanyState(
                signatoryKycState,
                signatoryReasons,
                "thc-signatory-reason"
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.card} aria-label="tenant overview">
      <div className={styles.top}>
        <div className={styles.left}>
          {percentageLoading ? (
            // Ring and label placeholders while the score loads
            <div className={styles.percentSkeleton} role="status">
              <span className="ds-visually-hidden">{_t("loading")}</span>
              <Skeleton
                width={90}
                height={90}
                radius="circle"
                className={styles.skeletonOnDark}
              />
              <Skeleton
                width={60}
                height={12}
                radius="pill"
                className={styles.skeletonOnDark}
              />
            </div>
          ) : (
            <ProfileCompletionPercentage
              percent={percent}
              loading={percentageLoading}
            />
          )}
          <div className={styles.missing}>
            <div className={styles.missingText}>
              {missingDocs.length > 0
                ? _t(
                    "docsAwayFromComplete",
                    `${missingDocs.length} documents away from a complete profile`,
                    undefined,
                    { count: missingDocs.length }
                  )
                : null}
            </div>
            <div className={styles.chips}>
              {visibleDocs.map((d, i) => (
                <Badge key={i} tone="onDark">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  {_t(
                    typeof d === "string" ? d : d[0],
                    typeof d === "string" ? d : d[0],
                    "fixed"
                  )}
                </Badge>
              ))}
              {missingDocs.length > 3 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className={styles.onDark}
                  onClick={() => setExpanded((v) => !v)}
                >
                  {expanded
                    ? _t("less", "Less")
                    : `+${missingDocs.length - 3} ${_t("more", "more")}`}
                </Button>
              )}
            </div>
            {explainedDocsCount > 0 && (
              <div className={styles.explainedDocs}>
                {_t(
                  "missingDocExplainedCount",
                  "{{count}} explained to landlords",
                  "translation",
                  {
                    count: explainedDocsCount,
                  }
                )}
              </div>
            )}
          </div>
        </div>
        <aside
          className={`${styles.note} ${user?.skor_protect ? styles.noteWithBadge : ""}`}
          aria-labelledby="landlord-note-title"
        >
          <div className={styles.noteHeader}>
            {user?.skor_protect && <SkorProtectBadge />}
            <span id="landlord-note-title" className={styles.noteTitle}>
              {_t("aNoteToLandlords", "A note to landlords")}
            </span>
            {!readOnly &&
              (disabled ? (
                <div
                  style={{ display: "inline-flex", alignItems: "center" }}
                  data-tooltip-id="thc-edit-tooltip"
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    iconStart={<Icon name="edit" size={16} />}
                    className={styles.onDark}
                    onClick={onEditLandlordNote}
                    disabled={disabled}
                    aria-label={_t("edit", "Edit")}
                  />
                  {renderTooltip("thc-edit-tooltip")}
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  iconStart={<Icon name="edit" size={16} />}
                  className={styles.onDark}
                  onClick={onEditLandlordNote}
                  aria-label={_t("edit", "Edit")}
                />
              ))}
          </div>
        </aside>
      </div>

      <div className={styles.grid}>
        <KycBox {...kycBoxProps}>
          <div className={styles.boxLabel}>
            {_t("verification", "Verification")}
          </div>
          <div className={styles.boxValue}>
            {isKycFailed ? (
              <span
                data-tooltip-id="thc-kyc-error"
                className={styles.kycErrorTrigger}
              >
                <span className={styles.kycErrorIcon} aria-hidden="true">
                  <img
                    src={
                      kycState === "review"
                        ? "/images/warning.svg"
                        : "/images/high-risk.svg"
                    }
                    alt=""
                    width="16"
                    height="16"
                  />
                </span>
                <span>{_t(kycState)}</span>
              </span>
            ) : (
              <span className={styles.kycErrorTrigger}>
                <span className={styles.kycErrorIcon} aria-hidden="true">
                  {kycState == "approved" && (
                    <img
                      src="/images/trusted.svg"
                      alt=""
                      width="16"
                      height="16"
                    />
                  )}
                </span>
                <span>{_t(kycState)}</span>
              </span>
            )}{" "}
            {!readOnly &&
              !isKycApproved &&
              (disabled ? (
                <div
                  style={{ display: "inline-block", marginLeft: "auto" }}
                  data-tooltip-id="thc-redo-tooltip"
                >
                  <Button
                    {...kycAction}
                    iconStart={redoIcon}
                    onClick={onRedoKyc}
                    disabled={disabled}
                  >
                    {_t("redo", "Redo")}
                  </Button>
                  {renderTooltip("thc-redo-tooltip")}
                </div>
              ) : (
                <Button {...kycAction} iconStart={redoIcon} onClick={onRedoKyc}>
                  {_t("redo", "Redo")}
                </Button>
              ))}
            {isKycFailed && kycReviewReason ? (
              <ReactTooltip
                id="thc-kyc-error"
                place="bottom"
                className={styles.kycTooltip}
                render={() => (
                  <div>
                    <p className={styles.kycTooltipText}>{kycReviewReason}</p>
                  </div>
                )}
              />
            ) : null}
          </div>
        </KycBox>
        <div className={styles.box}>
          <div className={styles.boxLabel}>
            {_t("bankBehaviour", "Bank behaviour")}
          </div>
          <div className={styles.boxValue}>
            {hasBankError ? (
              <span
                data-tooltip-id="thc-bank-error"
                className={styles.kycErrorTrigger}
              >
                <span className={styles.kycErrorIcon} aria-hidden="true">
                  <img src="/images/high-risk.svg" alt="" />
                </span>
                <span>{_t("error", "Error")}</span>
              </span>
            ) : (
              <span className={!bankConnected ? styles.dimmed : undefined}>
                {bankConnected
                  ? _t("banklinked", "Linked")
                  : _t("banknotLinked", "Not linked")}
              </span>
            )}{" "}
            {!readOnly &&
              (hasBankError ? (
                disabled ? (
                  <div
                    style={{ display: "inline-block", marginLeft: "auto" }}
                    data-tooltip-id="thc-bank-retry-tooltip"
                  >
                    <Button
                      {...strongAction}
                      iconStart={redoIcon}
                      onClick={onConnectBank}
                      disabled={disabled}
                    >
                      {_t("redo", "Redo")}
                    </Button>
                    {renderTooltip("thc-bank-retry-tooltip")}
                  </div>
                ) : (
                  <Button
                    {...strongAction}
                    iconStart={redoIcon}
                    onClick={onConnectBank}
                  >
                    {_t("redo", "Redo")}
                  </Button>
                )
              ) : (
                !bankConnected &&
                (disabled ? (
                  <div
                    style={{ display: "inline-block", marginLeft: "auto" }}
                    data-tooltip-id="thc-connect-tooltip"
                  >
                    <Button
                      {...strongAction}
                      iconStart={<Icon name="link" size={14} />}
                      onClick={onConnectBank}
                      disabled={disabled}
                    >
                      {_t("connect", "Connect")}
                    </Button>
                    {renderTooltip("thc-connect-tooltip")}
                  </div>
                ) : (
                  <Button
                    {...strongAction}
                    iconStart={<Icon name="link" size={14} />}
                    onClick={onConnectBank}
                  >
                    {_t("connect", "Connect")}
                  </Button>
                ))
              ))}
            {hasBankError && bankErrorText ? (
              <ReactTooltip
                id="thc-bank-error"
                place="bottom"
                className={styles.kycTooltip}
                render={() => (
                  <div>
                    {bankErrorTitle ? (
                      <p className={styles.kycTooltipTitle}>{bankErrorTitle}</p>
                    ) : null}
                    <p className={styles.kycTooltipText}>{bankErrorText}</p>
                  </div>
                )}
              />
            ) : null}
          </div>
        </div>
        <div className={styles.box}>
          <div className={styles.boxLabel}>
            {_t("rentalBarometer", "Rental barometer")}
          </div>
          <div
            className={`${styles.boxValue} ${isRentalElevated ? styles.danger : ""}`}
          >
            {hasRentalStatus ? (
              _t(normalizedRentalStatus.toLowerCase(), normalizedRentalStatus)
            ) : (
              <>
                <span className={styles.danger}>{_t("notSet", "Not set")}</span>
                {!readOnly &&
                  (disabled ? (
                    <div
                      style={{ display: "inline-block", marginLeft: "auto" }}
                      data-tooltip-id="thc-target-rent-tooltip"
                    >
                      <Button
                        {...strongAction}
                        onClick={onSetTargetRent}
                        disabled={disabled}
                      >
                        {_t("set", "Set")}
                      </Button>
                      {renderTooltip("thc-target-rent-tooltip")}
                    </div>
                  ) : (
                    <Button {...strongAction} onClick={onSetTargetRent}>
                      {_t("set", "Set")}
                    </Button>
                  ))}
              </>
            )}
          </div>
        </div>
        {isGuarantorFor ? (
          <div
            className={styles.box}
            onClick={canOpenGuarantorFor ? onOpenGuarantor : undefined}
            style={canOpenGuarantorFor ? { cursor: "pointer" } : undefined}
            role={canOpenGuarantorFor ? "button" : undefined}
            tabIndex={canOpenGuarantorFor ? 0 : undefined}
            onKeyDown={
              canOpenGuarantorFor
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onOpenGuarantor();
                    }
                  }
                : undefined
            }
            aria-label={_t("guarantorFor", "Guarantor for")}
          >
            <div className={styles.boxLabel}>
              {_t("guarantorFor", "Guarantor for")}
            </div>
            <div className={styles.boxValue}>
              {guarantorForCount}{" "}
              {canOpenGuarantorFor && (
                <Button
                  {...weakAction}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenGuarantor();
                  }}
                >
                  {_t("viewDetails", "View details")}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div
            className={styles.box}
            onClick={!readOnly && !disabled ? onAddGuarantor : undefined}
            style={
              !readOnly && !disabled && typeof onAddGuarantor === "function"
                ? { cursor: "pointer" }
                : undefined
            }
            role={
              !readOnly && !disabled && typeof onAddGuarantor === "function"
                ? "button"
                : undefined
            }
            tabIndex={
              !readOnly && !disabled && typeof onAddGuarantor === "function"
                ? 0
                : undefined
            }
            onKeyDown={
              !readOnly && !disabled && typeof onAddGuarantor === "function"
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onAddGuarantor();
                    }
                  }
                : undefined
            }
            aria-label={_t("guarantors", "Guarantors")}
            data-tooltip-id={disabled ? "thc-guarantor-tooltip" : undefined}
          >
            <div className={styles.boxLabel}>
              {_t("guarantors", "Guarantors")}
            </div>
            <div className={styles.boxValue}>
              {guarantorValueLabel}{" "}
              {!readOnly &&
                (disabled ? (
                  <div style={{ display: "inline-block", marginLeft: "auto" }}>
                    <Button
                      {...weakAction}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddGuarantor?.(e);
                      }}
                      disabled={disabled}
                    >
                      {guarantorCtaLabel}
                    </Button>
                  </div>
                ) : (
                  <Button
                    {...weakAction}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddGuarantor?.(e);
                    }}
                  >
                    {guarantorCtaLabel}
                  </Button>
                ))}
            </div>
            {renderTooltip("thc-guarantor-tooltip")}
          </div>
        )}
        {!isGuarantorFor && (
          <div
            className={styles.box}
            onClick={!readOnly && !disabled ? onAddFlatmate : undefined}
            style={
              !readOnly && !disabled && typeof onAddFlatmate === "function"
                ? { cursor: "pointer" }
                : undefined
            }
            role={
              !readOnly && !disabled && typeof onAddFlatmate === "function"
                ? "button"
                : undefined
            }
            tabIndex={
              !readOnly && !disabled && typeof onAddFlatmate === "function"
                ? 0
                : undefined
            }
            onKeyDown={
              !readOnly && !disabled && typeof onAddFlatmate === "function"
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onAddFlatmate();
                    }
                  }
                : undefined
            }
            aria-label={_t("applications", "Applications")}
            data-tooltip-id={disabled ? "thc-flatmate-tooltip" : undefined}
          >
            <div className={styles.boxHeader}>
              <div className={styles.boxLabel}>
                {_t("applications", "Applications")}
              </div>
              {pendingActionCount > 0 && (
                <Button
                  variant="secondary"
                  size="sm"
                  iconStart={
                    <span
                      className={styles.pendingActionDot}
                      aria-hidden="true"
                    />
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    onPendingActionClick?.(e);
                  }}
                >
                  {pendingActionCount} {pendingActionLabel}
                </Button>
              )}
            </div>
            <div className={styles.boxValue}>
              {applicationValueLabel}{" "}
              {!readOnly &&
                (disabled ? (
                  <div style={{ display: "inline-block", marginLeft: "auto" }}>
                    <Button
                      {...weakAction}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddFlatmate?.(e);
                      }}
                      disabled={disabled}
                    >
                      {applicationCtaLabel}
                    </Button>
                  </div>
                ) : (
                  <Button
                    {...weakAction}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddFlatmate?.(e);
                    }}
                  >
                    {applicationCtaLabel}
                  </Button>
                ))}
            </div>
            {renderTooltip("thc-flatmate-tooltip")}
          </div>
        )}
      </div>
      <Modal
        open={kycApprovedOpen}
        onClose={() => setKycApprovedOpen(false)}
        size="sm"
        icon={
          <span className={styles.kycApprovedIcon}>
            <Icon name="shieldCheck" size={24} />
          </span>
        }
        title={_t("identityVerified")}
        description={_t("yourKycIsApprovedText")}
        actionsLayout="stack"
        actions={
          <>
            <Button
              variant="primary"
              size="lg"
              block
              onClick={() => setKycApprovedOpen(false)}
            >
              {_t("gotIt")}
            </Button>
            <Button
              variant="link"
              size="sm"
              className={styles.kycRedoLink}
              disabled={disabled}
              onClick={() => {
                setKycApprovedOpen(false);
                onRedoKyc?.();
              }}
            >
              {_t("iWantToRedoKyc")}
            </Button>
          </>
        }
      />
    </section>
  );
};

export default TenantHeaderCard;
